import { useId, type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { PageSection } from '@/features/jobs/details/components/PageLayout'
import { SectionHeadline } from '@/components/SectionHeadline'
import {
  CardSubtitle,
  CardList,
  CardListItem,
  CardItemContent,
  CardTitle,
  CardItemIcon,
} from '@/components/Card'
import { StyledLink } from '@/components/Link'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import type { IconName } from '@/components/Icon'
import { DEFAULT, match } from '@/utils/pattern-match-util'
import type { JobScopeChangesDisplayViewModel } from '@/features/jobs/details/JobScopeChangesViewModel'
import {
  formatJobScopeChangeStatus,
  formatJobScopeChangeDescription,
} from '@/features/jobs/job-scope-changes/job-scope-change-formatters'

export interface JobScopeChangesSectionProps {
  children?: ReactNode
  title?: string
  testId?: string
}

/**
 * The JobScopeChangesSection
 */
export function JobScopeChangesSection({
  children,
  title = 'Service updates',
  testId,
}: JobScopeChangesSectionProps) {
  const headlineId = useId()

  return (
    <PageSection testId={testId}>
      <SectionHeadline id={headlineId}>{title}</SectionHeadline>
      <CardList aria-labelledby={headlineId}>{children}</CardList>
    </PageSection>
  )
}

interface JobScopeChangeProps {
  title?: string
  description?: string
  actionText: 'Review' | 'View'
  icon: IconName
  onAction?: () => unknown
  testId?: string
}

/**
 * Job Scope Change
 */
export function JobScopeChange({
  title,
  description,
  actionText: action,
  icon,
  onAction,
  testId,
}: JobScopeChangeProps) {
  const isSpacious = useIsSpacious()

  return (
    <CardListItem testId={testId} textValue={title} onAction={onAction}>
      <CardItemContent
        isInteractive
        {...(isSpacious && {
          icon: <CardItemIcon name={icon} variant="surface-background" />,
          extras: <StyledLink onPress={onAction}>{action}</StyledLink>,
        })}
      >
        <CardTitle>{title}</CardTitle>
        <CardSubtitle variant="label">{description}</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface JobScopeChangeRequestsSectionContainerProps {
  vm: JobScopeChangesDisplayViewModel
}

/**
 * Job scope changes section container
 */
export const JobScopeChangesSectionContainer = observer(
  function JobScopeChangeRequestsSectionContainer({
    vm,
  }: JobScopeChangeRequestsSectionContainerProps) {
    if (!vm.jobScopeChanges?.length) {
      return null
    }

    return (
      <>
        <JobScopeChangesSection testId="job-scope-changes-section">
          {vm.jobScopeChanges.map((jobScopeChange) => (
            <JobScopeChange
              key={jobScopeChange.id}
              testId={`job-scope-change-item-${jobScopeChange.id}`}
              title={formatJobScopeChangeStatus(jobScopeChange.status)}
              description={formatJobScopeChangeDescription(
                jobScopeChange.status,
                jobScopeChange.createdAt,
              )}
              actionText={
                jobScopeChange.status === 'PendingClientApproval'
                  ? 'Review'
                  : 'View'
              }
              icon={match(jobScopeChange.status, {
                Accepted: 'tick',
                Rejected: 'close',
                [DEFAULT]: 'alert',
              })}
              onAction={() => vm.show(jobScopeChange)}
            />
          ))}
        </JobScopeChangesSection>
      </>
    )
  },
)
